/* Exteral */
import {
  memo, forwardRef, useCallback, useRef, useEffect, useState,
} from 'react';
import PropTypes from 'prop-types';

/* Utils */
import { useKeyBoardState, useSetKeyBoardState } from '../../providers/KeyBoardStateProvider';
import { noOp } from '../../utils/functions';
import platform from '../../utils/platform';
import { useOnKeyDown } from '../../utils/keyDownEvents';

const {
  navigation: { enter },
  virtualKeyboard: { cancel, done },
} = platform.keyCodes;

const KEY_CODES_TO_COMPLETE = new Set([cancel, enter, done]);
const CANCEL_KEY_CODES = [cancel];

const Input = forwardRef(({
  value,
  className,
  type,
  placeholder,
  disabled,
  onChange,
  onKeyDown,
  onFocus,
  onBlur,
}, ref) => {
  const innerRef = useRef();
  const isKeyboardVisible = useKeyBoardState();
  const setProviderKeyboardState = useSetKeyBoardState();
  const [keyboardState, setKeyboardState] = useState(isKeyboardVisible);

  const handleCancel = useCallback((event) => {
    innerRef.current.blur();
    event.preventDefault();
  }, []);

  useOnKeyDown({
    listener: handleCancel,
    forKeyCodes: CANCEL_KEY_CODES,
    when: keyboardState,
  });

  const handleKeyDown = useCallback((event) => {
    if (KEY_CODES_TO_COMPLETE.has(event.keyCode)) {
      innerRef.current.blur();
    }
    onKeyDown(event);
  }, [onKeyDown]);

  const handleFocus = useCallback((event) => {
    setKeyboardState(true);
    onFocus(event);
  }, [onFocus, setKeyboardState]);

  const handleBlur = useCallback((event) => {
    setKeyboardState(false);
    onBlur(event);
  }, [onBlur, setKeyboardState]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setProviderKeyboardState(keyboardState);
    }, 300);

    return () => {
      clearTimeout(timeout);
    };
  }, [keyboardState, setProviderKeyboardState]);

  useEffect(() => {
    const currentRef = ref;
    if (typeof currentRef === 'function') {
      currentRef(innerRef.current);
      return () => {
        currentRef(undefined);
      };
    }
    if (currentRef) {
      currentRef.current = innerRef.current;
      return () => {
        currentRef.current = undefined;
      };
    }
    return () => {};
  }, [ref]);

  return (
    <input
      value={value}
      type={type}
      className={`dtv-html5-c-input ${className}`}
      placeholder={placeholder}
      disabled={disabled}
      onChange={onChange}
      onKeyDown={handleKeyDown}
      onFocus={handleFocus}
      onBlur={handleBlur}
      ref={innerRef}
    />
  );
});

Input.propTypes = {
  value: PropTypes.string,
  type: PropTypes.string.isRequired,
  className: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  onKeyDown: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
};

Input.defaultProps = {
  value: '',
  className: '',
  placeholder: '',
  disabled: false,
  onChange: noOp,
  onKeyDown: noOp,
  onFocus: noOp,
  onBlur: noOp,
};

export default memo(Input);
